// @ts-check
export { get, post, put };

const XSRF_TOKEN_NAME = "BH-XSRF-TOKEN";

/**
 * @param {string} url
 * @param {string} contentType
 * @param {number=} timeout
 * @param {string=} authRedirectUrl
 * @return {Promise<Response>}
 */
async function get(url, contentType, timeout, authRedirectUrl) {
  if (authRedirectUrl) {
    return handleRedirect(rejectOnError(getFetch(url, contentType, timeout)), authRedirectUrl);
  }
  return rejectOnError(getFetch(url, contentType, timeout));
}

/**
 * @param {string} url
 * @param {*} payload
 * @param {number=} timeout
 * @param {string=} authRedirectUrl
 * @return {Promise<Response>}
 */
async function post(url, payload, timeout, authRedirectUrl) {
  if (authRedirectUrl) {
    return handleRedirect(rejectOnError(postFetch(url, payload, timeout)), authRedirectUrl);
  }
  return rejectOnError(postFetch(url, payload, timeout));
}

/**
 * @param {string} url
 * @param {*} payload
 * @param {number=} timeout
 * @param {string=} authRedirectUrl
 * @return {Promise<Response>}
 */
async function put(url, payload, timeout, authRedirectUrl) {
  if (authRedirectUrl) {
    return handleRedirect(rejectOnError(putFetch(url, payload, timeout)), authRedirectUrl);
  }
  return rejectOnError(putFetch(url, payload, timeout));
}

/**
 * @param {string} url
 * @param {string} contentType
 * @param {number=} timeout
 * @return {Promise<Response>}
 */
async function getFetch(url, contentType, timeout) {
  /** @type {RequestInit} */
  const paramGet = {
    credentials: "include",
    method: "GET",
    signal: getSignal(timeout),
    mode: "no-cors",
    headers: { "Content-Type": contentType }
  };

  return fetch(url, paramGet);
}

/**
 * @param {string} url
 * @param {*} payload
 * @param {number=} timeout
 * @returns {Promise<Response>}
 */
async function postFetch(url, payload, timeout) {
  const xsrfToken = getCookieByName(XSRF_TOKEN_NAME);
  const xsrfHeaderKey = `X-${XSRF_TOKEN_NAME}`;

  /** @type {RequestInit} */
  const paramPost = {
    credentials: "same-origin",
    method: "POST",
    signal: getSignal(timeout),
    headers: {
      "Content-Type": "application/json",
      [xsrfHeaderKey]: xsrfToken,
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify(payload)
  };

  return fetch(url, paramPost);
}

/**
 * @param {string} url
 * @param {*} payload
 * @param {number=} timeout
 * @returns {Promise<Response>}
 */
async function putFetch(url, payload, timeout) {
  const xsrfToken = getCookieByName(XSRF_TOKEN_NAME);
  const xsrfHeaderKey = `X-${XSRF_TOKEN_NAME}`;

  /** @type {RequestInit} */
  const paramPost = {
    credentials: "same-origin",
    method: "PUT",
    signal: getSignal(timeout),
    headers: {
      "Content-Type": "application/json",
      [xsrfHeaderKey]: xsrfToken,
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify(payload)
  };

  return fetch(url, paramPost);
}

/**
 * @param {Promise<Response>} response
 * @return {Promise<Response>}
 */
async function rejectOnError(response) {
  const res = await response;
  if (!res.ok) {
    return Promise.reject(res);
  }

  return response;
}

/**
 * @param {Promise<Response>} response
 * @param {string} authRedirectUrl
 * @return {Promise<Response>}
 */
async function handleRedirect(response, authRedirectUrl) {
  try {
    return await response;
  } catch (e) {
    if (e instanceof Response && (e.status === 401 || e.status === 403)) {
      window.location.assign(authRedirectUrl);
    }
    return Promise.reject(e);
  }
}

/**
 * @param {number} timeout in ms
 * @return {AbortSignal}
 */
function getSignal(timeout) {
  let timeoutSignal;
  if (timeout) {
    const timeoutAbortController = new AbortController();
    setTimeout(() => { return timeoutAbortController.abort(); }, timeout);
    timeoutSignal = timeoutAbortController.signal;
  }
  return timeoutSignal;
}

/**
 * @param {string} name
 * @returns {string}
 */
function getCookieByName(name) {
  const match = document.cookie.match(new RegExp(`${name}=([^;]+)`));
  if (match) {
    return match[1];
  }
  return "";
}
